import { getPlanPermission } from "@utils/permissions/plan-permissions";
import { AdmDataGrid } from "@components/common/data-grid/adm-data-grid";
import { RequirePermission } from "@components/common/require-permission";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  PlanCreationMode,
  SubjectEntityIdTypes,
  SubjectType,
  SubjectTypeProps,
} from "@custom-types/base-types";
import { Spinner } from "@components/common/spinner/spinner";
import { ContentLayout } from "@pages/content-layout";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { allPlansSelector } from "@store/plans/plans-selector";
import { usePlansTableColumns } from "@components/plans-table/use-plans-table-columns";
import { plansStateSelector } from "@store/plans/plans-slice";
import {
  devFeaturesStateSelector,
  setShouldShowAllPlans,
} from "@store/dev-features/dev-features-slice";
import { fetchPlans } from "@store/plans/plans-thunks";
import { AdditionalPlanTableRow } from "@components/plans-table/additional-plan-table-row";
import { Add } from "@mui/icons-material";
import { useAppNavigation } from "@router/use-app-navigation";
import { getPlansTableFilters } from "@components/plans-table/plans-table-filter";
import { useHardwareTableColumns } from "@components/plans-table/use-hardware-table-columns";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PlanEvents } from "@utils/track-event/track-event-list";
import { Button } from "@flat-ui";
import { getPlanTableType } from "@utils/data-grid-utils/plans-data-grid-cells";
import { appStateSelector } from "@store/app/app-slice";
import { BulkPlanActions } from "@components/plans-table/bulk-plan-actions";

interface Props extends SubjectTypeProps {
  /** ID of the subject entity that the plan tab is related to */
  subjectId: SubjectEntityIdTypes;
}

/** Component showing the list of plans belong to a subjectType */
export function PlansTable({
  subjectType,
  subjectId,
}: Props): React.JSX.Element {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const { navigateToPlanCreation } = useAppNavigation();
  const { handleErrorWithToast } = useErrorHandler();
  const [shouldShowErrorMessage, setShouldShowErrorMessage] =
    useState<boolean>(false);

  const errorMessage = `Could not load ${
    subjectType === "DEVICE" ? "licenses" : "plans"
  }`;

  /**
   * The hardware licenses table shows its own columns, so the filters that classify a plan by
   * what it grants have no column to act on there.
   */
  const hasPlanColumn = subjectType !== "DEVICE";

  const planColumns = usePlansTableColumns();
  const hardwareColumns = useHardwareTableColumns();

  const plans = useAppSelector(allPlansSelector());
  const { fetchingPlan } = useAppSelector(plansStateSelector);
  const { shouldShowAllPlans, shouldAllowBulkOperations } = useAppSelector(
    devFeaturesStateSelector
  );
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);

  // A selection must not outlive the bulk operations, or it would be acted on when they are back on
  useEffect(() => {
    if (!shouldAllowBulkOperations) {
      setSelectedPlanIds([]);
    }
  }, [shouldAllowBulkOperations]);
  const { isDevModeEnabled } = useAppSelector(appStateSelector);

  /**
   * Plans assigned to a single workspace member are not part of the workspace plans, but they are
   * still relevant to inspect, so they are listed together with the workspace plans as soon as the
   * "Show all plans" experimental feature is enabled
   */
  const visibleSubjectTypes: SubjectType[] =
    subjectType === "COMPANY" && shouldShowAllPlans
      ? ["COMPANY", "COMPANY_MEMBER"]
      : [subjectType];

  useEffect(() => {
    dispatch(fetchPlans({ subjectType, subjectId }))
      .unwrap()
      .catch((error) => {
        handleErrorWithToast({
          id: `PlanInfo-${Date.now().toString()}`,
          title: errorMessage,
          error,
        });
        setShouldShowErrorMessage(true);
      });
  }, [dispatch, handleErrorWithToast, subjectType, subjectId, errorMessage]);

  /** Tracks the intent and opens the page that assigns the requested kind of product */
  function requestAddingPlan(
    creationMode?: Exclude<PlanCreationMode, "hardware-license">
  ): void {
    trackEvent({
      name: PlanEvents.requestAddingPlan,
      props: {
        location: getPlanTableType(subjectType),
        creationMode: creationMode ?? "hardware-license",
      },
    });
    navigateToPlanCreation({ subjectType, subjectId, creationMode });
  }

  const visiblePlans = plans.filter((plan) =>
    visibleSubjectTypes.includes(plan.subject.type)
  );
  const selectedPlans = visiblePlans.filter(({ id }) =>
    selectedPlanIds.includes(id)
  );
  const tableType = getPlanTableType(subjectType);
  const addPlanPermission = getPlanPermission(subjectType, "add");

  if (fetchingPlan.allPlans === "pending") {
    return <Spinner />;
  }

  return (
    <ContentLayout contentSx={{ overflowY: "hidden" }}>
      <AdmDataGrid
        entities={visiblePlans}
        uniqueIdKey="id"
        tableType={getPlanTableType(subjectType)}
        columns={subjectType === "DEVICE" ? hardwareColumns : planColumns}
        errorMessage={errorMessage}
        shouldShowErrorMessage={shouldShowErrorMessage}
        filterGroups={getPlansTableFilters({
          shouldShowTypeFilter: hasPlanColumn,
          shouldShowDevModeFilters: isDevModeEnabled && hasPlanColumn,
          onSelectSceneSeats: () => dispatch(setShouldShowAllPlans(true)),
        })}
        rowDetails={(row) => (
          <AdditionalPlanTableRow
            key={row.id}
            planId={String(row.id)}
            subjectType={subjectType}
            tableType={getPlanTableType(subjectType)}
          />
        )}
        selectedRowIds={shouldAllowBulkOperations ? selectedPlanIds : undefined}
        onSelectedRowIdsChange={
          shouldAllowBulkOperations ? setSelectedPlanIds : undefined
        }
        leadingToolbarButtons={
          shouldAllowBulkOperations && (
            <BulkPlanActions
              subjectType={subjectType}
              selectedPlans={selectedPlans}
              tableType={tableType}
              onApplied={() => setSelectedPlanIds([])}
            />
          )
        }
        additionalToolbarButtons={
          subjectType === "DEVICE" ? (
            <RequirePermission permission={addPlanPermission}>
              <Button icon={<Add />} onClick={() => requestAddingPlan()}>
                Add License
              </Button>
            </RequirePermission>
          ) : (
            <>
              <RequirePermission permission={addPlanPermission}>
                <Button
                  icon={<Add />}
                  variant="secondary"
                  onClick={() => requestAddingPlan("feature")}
                >
                  Add Feature
                </Button>
              </RequirePermission>
              <RequirePermission permission={addPlanPermission}>
                <Button
                  icon={<Add />}
                  onClick={() => requestAddingPlan("subscription")}
                >
                  Add Subscription
                </Button>
              </RequirePermission>
            </>
          )
        }
        defaultSortModel={[{ field: "status", sort: "asc" }]}
        shouldForceSort={true}
      />
    </ContentLayout>
  );
}
